/**
 * Validation Utilities - Admin
 * 
 * Client-side validation functions for admin forms and user input.
 * 
 * @version 1.0.0
 */

/**
 * Email validation
 */
export const validateEmail = (email) => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};

/**
 * Phone validation (10 digits)
 */
export const validatePhone = (phone) => {
  const digits = phone.replace(/\D/g, '');
  return digits.length === 10;
};

/**
 * Amount validation
 */
export const validateAmount = (amount) => {
  const num = parseFloat(amount);
  return !isNaN(num) && num > 0;
};

/**
 * Required field validation
 */
export const validateRequired = (value) => {
  return value && value.toString().trim().length > 0;
};

/**
 * String length validation
 */
export const validateLength = (value, min = 1, max = 1000) => {
  const length = value.toString().length;
  return length >= min && length <= max;
};

/**
 * URL validation
 */
export const validateUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Password validation (minimum 8 characters)
 */
export const validatePassword = (password) => {
  return password && password.length >= 8;
};

/**
 * Username validation (alphanumeric and underscore)
 */
export const validateUsername = (username) => {
  const regex = /^[a-zA-Z0-9_]{3,20}$/;
  return regex.test(username);
};

/**
 * Validate form object
 */
export const validateForm = (formData, rules) => {
  const errors = {};

  Object.keys(rules).forEach((field) => {
    const value = formData[field];
    const fieldRules = rules[field];

    if (fieldRules.required && !validateRequired(value)) {
      errors[field] = `${field} is required`;
    } else if (fieldRules.email && value && !validateEmail(value)) {
      errors[field] = 'Invalid email format';
    } else if (fieldRules.phone && value && !validatePhone(value)) {
      errors[field] = 'Invalid phone number';
    } else if (fieldRules.minLength && value && !validateLength(value, fieldRules.minLength)) {
      errors[field] = `Minimum ${fieldRules.minLength} characters required`;
    } else if (fieldRules.maxLength && value && !validateLength(value, 0, fieldRules.maxLength)) {
      errors[field] = `Maximum ${fieldRules.maxLength} characters allowed`;
    }
  });

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
};

export default {
  validateEmail,
  validatePhone,
  validateAmount,
  validateRequired,
  validateLength,
  validateUrl,
  validatePassword,
  validateUsername,
  validateForm,
};
